import { updateUserFormSetting } from '@/lib/api/userFormSettingApi'
import type { RootState } from '@/redux/store'
import { BaseFileEnum, UserFormSetting } from '@/typings/domain'
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit'
import { fetchCoversByFolderThunk, fetchCoversByTagThunk } from './coverSlice'
import { copyFormThunk, createFormThunk, fetchFormThunk } from './formSlice'

export type UserFormSettingType = UserFormSetting & {
  token: string
}

const userFormSettingAdapter = createEntityAdapter<UserFormSettingType, string>({
  selectId: model => model.token,
})

export const userFormSettingSlice = createSlice({
  name: 'userFormSettings',
  initialState: userFormSettingAdapter.getInitialState(),
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(updateUserFormSettingFavoredThunk.fulfilled, (state, action) => {
        const { token, favored } = action.payload
        userFormSettingAdapter.upsertOne(state, { token, favored } as UserFormSettingType)
      })
      // -------- subscribe coverSlice thunks --------
      .addCase(fetchCoversByTagThunk.fulfilled, (state, action) => {
        const entities = action.payload.covers.map(cover => ({
          token: cover.token,
          ...cover.userFormSetting,
        }))
        userFormSettingAdapter.upsertMany(state, entities as UserFormSettingType[])
      })
      .addCase(fetchCoversByFolderThunk.fulfilled, (state, action) => {
        const entities = action.payload.covers.map(cover => ({
          token: cover.token,
          ...cover.userFormSetting,
        }))
        userFormSettingAdapter.upsertMany(state, entities as UserFormSettingType[])
      })
      // -------- subscribe formSlice thunks --------
      .addCase(fetchFormThunk.fulfilled, (state, action) => {
        const token = action.meta.arg.formToken
        const { userFormSetting } = action.payload
        userFormSettingAdapter.upsertOne(state, { token, ...userFormSetting } as UserFormSettingType)
      })
      .addCase(copyFormThunk.fulfilled, (state, action) => {
        const copiedForm = action.payload.newForm
        const { token, userFormSetting } = copiedForm
        userFormSettingAdapter.addOne(state, { token, ...userFormSetting } as UserFormSettingType)
      })
      .addCase(createFormThunk.fulfilled, (state, action) => {
        const { token, userFormSetting } = action.payload
        userFormSettingAdapter.addOne(state, { token, ...userFormSetting } as UserFormSettingType)
      })
  },
})

// -------- thunks --------

export const updateUserFormSettingFavoredThunk = createAsyncThunk<
  { token: string; favored: boolean; type?: BaseFileEnum },
  { token: string; favored: boolean; type?: BaseFileEnum }
>('formCovers/updateFormCoverFavored', async ({ token, favored, type }, thunkAPI) => {
  const { data } = await updateUserFormSetting({ attributes: [{ formId: token, favored }] })
  return data?.updateUserFormSetting
    ? { token, favored, type }
    : thunkAPI.rejectWithValue('failed to update favored status')
})

// -------- selectors --------

export const userFormSettingSelectors = userFormSettingAdapter.getSelectors<RootState>(state => state.userFormSettings)

export const selectUserFormSettingByFormToken = createSelector(
  (state: RootState) => state.userFormSettings,
  (_state: RootState, formToken: string) => formToken,
  (state, formToken) => userFormSettingAdapter.getSelectors().selectById(state, formToken)
)

export default userFormSettingSlice.reducer
